import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumb, Col, Container, Row } from 'react-bootstrap';
import type { IconType } from 'react-icons';
import {
  LuArrowRight,
  LuCalendar,
  LuCheck,
  LuClock,
  LuFileText,
  LuHandshake,
  LuMessageCircle,
  LuNetwork,
  LuSearch,
  LuShieldCheck,
  LuTarget,
  LuTrendingUp,
  LuUsers,
} from 'react-icons/lu';

import GradientCTA from '../app/components/GradientCTA';
import HeroAnimated from '../app/components/HeroAnimated';
import RevealSection from '../app/components/RevealSection';
import SbrIcon from '../app/components/SbrIcon';

const PEOPLE_IMG = `${process.env.PUBLIC_URL}/images/PeopleImage2.jpg`;
const SINGLE_PERSON_IMG = `${process.env.PUBLIC_URL}/images/SinglePerson.jpg`;

const heroFeatures: { title: string; body: string; Icon: IconType }[] = [
  {
    title: 'Dedicated Focus',
    body: 'Full attention on your mandate from day one.',
    Icon: LuTarget,
  },
  {
    title: 'Deep Market Reach',
    body: 'Proactive mapping across passive and active talent.',
    Icon: LuNetwork,
  },
  {
    title: 'Guaranteed Results',
    body: 'A proven process with clear milestones and outcomes.',
    Icon: LuShieldCheck,
  },
];

type RetainedBenefitTone = 'teal' | 'blue' | 'purple';

const retainedBenefits: {
  title: string;
  body: string;
  Icon: IconType;
  tone: RetainedBenefitTone;
}[] = [
  {
    title: 'Exclusive Commitment',
    body: 'Our team is dedicated exclusively to your search.',
    Icon: LuTarget,
    tone: 'teal',
  },
  {
    title: 'Access Top Talent',
    body: 'We engage high-caliber, passive candidates others can\u2019t reach.',
    Icon: LuUsers,
    tone: 'blue',
  },
  {
    title: 'Strategic Partnership',
    body: 'We act as an extension of your team, providing market insights and guidance.',
    Icon: LuTrendingUp,
    tone: 'purple',
  },
  {
    title: 'Risk Reduction',
    body: 'A replacement guarantee gives you confidence in every hire we deliver.',
    Icon: LuShieldCheck,
    tone: 'teal',
  },
  {
    title: 'Efficiency & Speed',
    body: 'Focused resources and a proven process save you time and reduce downtime.',
    Icon: LuClock,
    tone: 'blue',
  },
];

type RetainedProcessColor = 'teal' | 'blue';

const retainedProcessSteps: {
  n: string;
  title: string;
  body: string;
  Icon: IconType;
  color: RetainedProcessColor;
}[] = [
  {
    n: '1',
    title: 'Discovery',
    body: 'We deeply understand your business, goals, role, and success profile.',
    Icon: LuSearch,
    color: 'teal',
  },
  {
    n: '2',
    title: 'Research & Map',
    body: 'We map the market, identify target companies, and build a tailored talent strategy.',
    Icon: LuNetwork,
    color: 'blue',
  },
  {
    n: '3',
    title: 'Engage',
    body: 'We confidentially engage and assess top passive and active candidates.',
    Icon: LuMessageCircle,
    color: 'teal',
  },
  {
    n: '4',
    title: 'Evaluate & Present',
    body: 'We deliver a shortlist of highly qualified, vetted candidates.',
    Icon: LuFileText,
    color: 'blue',
  },
  {
    n: '5',
    title: 'Support & Close',
    body: 'We guide interviews, negotiations, and offer acceptance.',
    Icon: LuHandshake,
    color: 'teal',
  },
  {
    n: '6',
    title: 'Guarantee',
    body: 'We stand behind our work with a replacement guarantee.',
    Icon: LuShieldCheck,
    color: 'blue',
  },
];

const whyRetainedItems = [
  'Ideal for C-suite, VP, Director, and other mission-critical roles',
  'Deep, focused search with no distractions',
  'Customized strategy tailored to your unique needs',
  'Transparent communication and full visibility',
];

const searchCompareRows: { feature: string; retained: string; contingent: string }[] = [
  { feature: 'Dedicated Resources', retained: '100% Committed', contingent: 'Shared / Limited' },
  { feature: 'Candidate Quality', retained: 'High-Caliber & Passive', contingent: 'Active Candidates' },
  { feature: 'Time to Fill', retained: 'Optimized', contingent: 'Variable' },
  { feature: 'Market Insights', retained: 'In-Depth & Strategic', contingent: 'Limited' },
  { feature: 'Guarantee', retained: 'Yes', contingent: 'Rare / Limited' },
  { feature: 'Best For', retained: 'Critical, High-Impact Roles', contingent: 'Standard / Low-Risk Roles' },
];

export default function RetainedSearchPage() {
  return (
    <>
      <section className="hero-pattern text-white">
        <Container className="py-5 py-lg-6">
          <Breadcrumb className="solution-hero-breadcrumb mb-4">
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/' }}>
              Solutions
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Retained Search</Breadcrumb.Item>
          </Breadcrumb>

          <Row className="align-items-stretch gy-4">
            <Col lg={6} className="d-flex flex-column">
              <HeroAnimated>
                <h1 data-hero-item className="display-5 fw-bold mb-3">
                  Retained Search.
                  <span className="d-block text-gradient-teal-blue mt-2">Total Commitment.</span>
                </h1>
                <p data-hero-item className="lead text-white-50 mb-4 pe-lg-2">
                  When leadership hires demand certainty, retained search delivers dedicated focus,
                  deep market intelligence, and a true partnership built for exceptional outcomes.
                </p>
                <Row data-hero-item className="g-3 mt-auto">
                  {heroFeatures.map((f) => (
                    <Col sm={4} key={f.title}>
                      <div className="solution-hero-feature">
                        <div className="solution-hero-feature__icon">
                          <SbrIcon icon={f.Icon} size={22} />
                        </div>
                        <div className="solution-hero-feature__copy">
                          <div className="solution-hero-feature__title">{f.title}</div>
                          <p className="solution-hero-feature__body">{f.body}</p>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </HeroAnimated>
            </Col>
            <Col lg={6}>
              <RevealSection className="h-100">
                <div className="solution-hero-media h-100 solution-hero-media--retained">
                  <img
                    src={PEOPLE_IMG}
                    alt="Business professionals in a meeting around a conference table"
                    className="solution-hero-media__img"
                    width={800}
                    height={600}
                    decoding="async"
                  />
                  <div className="solution-hero-media__promise">
                    <div className="solution-hero-media__promise-main">
                      <div className="solution-hero-media__promise-title">Our Retained Promise</div>
                      <div className="solution-hero-media__promise-accent" aria-hidden />
                      <p className="solution-hero-media__promise-body mb-0">
                        A true partnership. A proven process. Exceptional outcomes.
                      </p>
                    </div>
                    <div className="solution-hero-media__promise-icon" aria-hidden>
                      <SbrIcon icon={LuShieldCheck} size={40} />
                    </div>
                  </div>
                </div>
              </RevealSection>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="bg-white py-5 py-lg-6">
        <RevealSection>
          <Container>
            <div className="sbr-section-header text-center mb-5">
              <h2 className="sbr-section__title fw-bold mb-0">
                The Benefits of Our Retained Search Model
              </h2>
            </div>
            <div className="rs-benefits">
              {retainedBenefits.map((b, index) => (
                <Fragment key={b.title}>
                  {index > 0 && <div className="rs-benefits__divider" aria-hidden />}
                  <div className={`rs-benefits__item rs-benefits__item--${b.tone}`}>
                    <div className="rs-benefits__icon" aria-hidden>
                      <SbrIcon icon={b.Icon} size={26} />
                    </div>
                    <h3 className="rs-benefits__title">{b.title}</h3>
                    <p className="rs-benefits__body mb-0">{b.body}</p>
                  </div>
                </Fragment>
              ))}
            </div>
          </Container>
        </RevealSection>
      </section>

      <section className="bg-sbr-light py-5 py-lg-6">
        <RevealSection>
          <Container>
            <div className="rs-process text-center">
              <h2 className="rs-process__title sbr-section__title fw-bold mb-0">
                Our Retained Search Process
              </h2>
              <div className="rs-process__track">
                {retainedProcessSteps.map((s, index) => (
                  <Fragment key={s.title}>
                    <article className={`rs-process-step rs-process-step--${s.color}`}>
                      <div className="rs-process-step__icon" aria-hidden>
                        <SbrIcon icon={s.Icon} size={26} />
                      </div>
                      <h3 className="rs-process-step__heading">
                        {s.n}. {s.title}
                      </h3>
                      <p className="rs-process-step__body mb-0">{s.body}</p>
                    </article>
                    {index < retainedProcessSteps.length - 1 && (
                      <div className="rs-process__connector" aria-hidden />
                    )}
                  </Fragment>
                ))}
              </div>
            </div>
          </Container>
        </RevealSection>
      </section>

      <section className="bg-sbr-light py-5 py-lg-6">
        <RevealSection>
          <Container>
            <Row className="gy-4 align-items-stretch cs-benefits-row">
              <Col lg={6} className="d-flex">
                <div className="cs-why-choose w-100">
                  <h2 className="cs-why-choose__title sbr-card__title mb-4">Why Retained Search?</h2>
                  <p className="cs-why-choose__lead mb-4">
                    When the stakes are high, you need more than resumes — you need a trusted partner with
                    the time, resources, and commitment to deliver exceptional talent.
                  </p>
                  <ul className="cs-why-choose__list list-unstyled mb-0">
                    {whyRetainedItems.map((t) => (
                      <li key={t} className="cs-why-choose__item">
                        <span className="cs-why-choose__check" aria-hidden>
                          <SbrIcon icon={LuCheck} size={14} />
                        </span>
                        <span className="cs-why-choose__text">{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Col>
              <Col lg={6} className="d-flex">
                <div className="cs-ideal-for rs-compare-panel w-100">
                  <h2 className="cs-ideal-for__title sbr-card__title mb-4">Retained vs. Contingent</h2>
                  <div className="table-responsive">
                    <table className="rs-compare__table table mb-0">
                      <thead>
                        <tr>
                          <th scope="col">Feature</th>
                          <th scope="col">Retained Search (SBR2TH)</th>
                          <th scope="col">Contingent Search</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchCompareRows.map((row) => (
                          <tr key={row.feature}>
                            <th scope="row">{row.feature}</th>
                            <td className="rs-compare__retained">{row.retained}</td>
                            <td className="rs-compare__contingent">{row.contingent}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </RevealSection>
      </section>

      <section className="py-4 py-lg-5">
        <RevealSection>
          <Container>
            <div className="rs-success-story">
              <div className="rs-success-story__copy">
                <p className="rs-success-story__eyebrow">Success Story</p>
                <h2 className="rs-success-story__title">
                  Building a Leadership Team That Drove $100M in Growth
                </h2>
                <p className="rs-success-story__lead">
                  We partnered with a leading technology company to build out their executive leadership
                  team. The result: stronger leadership, faster growth, and lasting impact.
                </p>
                <Link className="rs-success-story__cta" to="/resources">
                  View Case Study
                  <SbrIcon icon={LuArrowRight} size={18} className="ms-2" aria-hidden />
                </Link>
              </div>

              <div className="rs-success-story__media">
                <img
                  src={SINGLE_PERSON_IMG}
                  alt="Professional leader smiling with arms crossed"
                  className="rs-success-story__photo"
                  width={400}
                  height={480}
                  decoding="async"
                />
              </div>

              <div className="rs-success-story__stats">
                <div className="rs-success-story__stat">
                  <div className="rs-success-story__stat-value">6</div>
                  <p className="rs-success-story__stat-label mb-0">Leadership hires in 9 months</p>
                </div>
                <div className="rs-success-story__stat-divider" aria-hidden />
                <div className="rs-success-story__stat">
                  <div className="rs-success-story__stat-value">$100M+</div>
                  <p className="rs-success-story__stat-label mb-0">Revenue growth in year one</p>
                </div>
              </div>

              <blockquote className="rs-success-story__quote">
                <span className="rs-success-story__quote-mark" aria-hidden>
                  &ldquo;
                </span>
                <p className="rs-success-story__quote-text">
                  SBR2TH took the time to truly understand our needs and delivered leaders who have
                  transformed our organization. Their team felt like our team.
                </p>
                <footer className="rs-success-story__attribution">
                  <span className="rs-success-story__avatar" aria-hidden>
                    JR
                  </span>
                  <div>
                    <div className="rs-success-story__name">Jason Reynolds</div>
                    <div className="rs-success-story__role">CEO, Innovatech Solutions</div>
                  </div>
                </footer>
              </blockquote>
            </div>
          </Container>
        </RevealSection>
      </section>

      <GradientCTA
        icon={<SbrIcon icon={LuCalendar} size={26} />}
        title={
          <>
            <div>Ready to find the right leader for your team?</div>
            <div className="sbr-cta__subtitle fw-normal opacity-90 mt-1 mb-0">
              Let&apos;s discuss how our retained search model can deliver exceptional results.
            </div>
          </>
        }
      />
    </>
  );
}
